import { useCallback, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { FF_WORKFLOW_APP_BUILDER_MODE } from '@/consts/featureFlags';
import { WorkflowEditorMode } from '@/enums/workflow-editor-mode.enum';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

/**
 * One workflow's Canvas/Tool choice. sessionStorage survives a reload and dies with the tab.
 * The workflow id keeps a Tool tab from leaking onto a different workflow.
 */
const WORKFLOW_EDITOR_MODE_STORAGE_PREFIX = 'weave-workflow-editor-mode:';

interface WorkflowEditorModeStoreState {
  /** Workflow the in-memory mode was loaded for. Undefined until a workflow is open. */
  activeWorkflowId: string | undefined;
  workflowEditorMode: WorkflowEditorMode;
  reset: () => void;
}

function workflowEditorModeStorageKey(workflowId: string): string {
  return `${WORKFLOW_EDITOR_MODE_STORAGE_PREFIX}${workflowId}`;
}

/**
 * Canvas unless this workflow's stored choice is Tool and the app-builder flag is on.
 * A stored Tool value is ignored while the flag is off.
 */
function readPersistedWorkflowEditorMode(workflowId: string, isAppBuilderEnabled: boolean): WorkflowEditorMode {
  if (!isAppBuilderEnabled) {
    return WorkflowEditorMode.EditWorkflow;
  }
  try {
    const stored = sessionStorage.getItem(workflowEditorModeStorageKey(workflowId));
    if (stored === WorkflowEditorMode.AppBuilder) {
      return WorkflowEditorMode.AppBuilder;
    }
  } catch {
    // sessionStorage throws when it is disabled. Canvas is the safe mode.
  }
  return WorkflowEditorMode.EditWorkflow;
}

function persistWorkflowEditorMode(workflowId: string, mode: WorkflowEditorMode): void {
  try {
    sessionStorage.setItem(workflowEditorModeStorageKey(workflowId), mode);
  } catch {
    // The in-memory mode still updates. A reload just won't remember this choice.
  }
}

export const workflowEditorModeStore = create<WorkflowEditorModeStoreState>()(
  devtools(
    (set, get) => ({
      activeWorkflowId: undefined,
      workflowEditorMode: WorkflowEditorMode.EditWorkflow,
      reset: () => {
        const { activeWorkflowId } = get();
        if (activeWorkflowId) {
          persistWorkflowEditorMode(activeWorkflowId, WorkflowEditorMode.EditWorkflow);
        }
        set({ workflowEditorMode: WorkflowEditorMode.EditWorkflow });
      },
    }),
    {
      name: 'Workflow Editor Mode',
      enabled: import.meta.env.DEV,
    },
  ),
);

/**
 * Workflow editor mode for UI. App builder mode is only available when the feature flag
 * resolves to enabled; until then, switching to app-builder is ignored.
 *
 * The choice is stored per workflow in sessionStorage, so a reload of the same workflow
 * in the same tab returns to Tool, and a different workflow still opens on Canvas.
 */
export function useWorkflowEditorMode() {
  const { value: isWorkflowAppBuilderEnabled } = useFeatureFlag(FF_WORKFLOW_APP_BUILDER_MODE);
  // The open workflow is the route id (`/flow/:recipeId` and the embed variants). Keying on
  // that, not the in-memory recipe, keeps Tool from surviving into a different workflow.
  const { recipeId: workflowId } = useParams();

  const workflowEditorMode = workflowEditorModeStore((state) => state.workflowEditorMode);
  const resetWorkflowEditorMode = workflowEditorModeStore((state) => state.reset);

  // Before paint, so a reload doesn't flash Canvas and then jump to Tool.
  useLayoutEffect(() => {
    if (!workflowId || isWorkflowAppBuilderEnabled === undefined) {
      return;
    }
    workflowEditorModeStore.setState({
      activeWorkflowId: workflowId,
      workflowEditorMode: readPersistedWorkflowEditorMode(workflowId, isWorkflowAppBuilderEnabled),
    });
  }, [isWorkflowAppBuilderEnabled, workflowId]);

  const setWorkflowEditorMode = useCallback(
    (mode: WorkflowEditorMode) => {
      if (mode === WorkflowEditorMode.AppBuilder && !isWorkflowAppBuilderEnabled) {
        return;
      }
      if (workflowId) {
        persistWorkflowEditorMode(workflowId, mode);
      }
      workflowEditorModeStore.setState({
        activeWorkflowId: workflowId,
        workflowEditorMode: mode,
      });
    },
    [isWorkflowAppBuilderEnabled, workflowId],
  );

  return {
    workflowEditorMode,
    setWorkflowEditorMode,
    resetWorkflowEditorMode,
  };
}
